import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import SeoHead from '@/components/feature/SeoHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.discoveringmarche.com';

const PARTNER_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Professionisti Partner — Discovering Marche | Rete di fiducia per il tuo immobile nelle Marche',
    description: 'Scopri i professionisti partner di Discovering Marche: studio legale e notarile, impresa edile certificata. Un pacchetto completo per acquistare casa nelle Marche in totale trasparenza.',
    url: `${SITE_URL}/partner`,
    provider: {
      '@type': 'LocalBusiness',
      name: 'Discovering Marche',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Castelfidardo',
        addressRegion: 'AN',
        addressCountry: 'IT',
      },
    },
    areaServed: 'Marche, Italy',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Professionisti Partner', item: `${SITE_URL}/partner` },
    ],
  },
];

const TRUST_ITEMS = [
  { key: 'trasparenza', icon: 'ri-shield-check-line' },
  { key: 'qualita', icon: 'ri-medal-line' },
  { key: 'referente', icon: 'ri-user-star-line' },
  { key: 'multilingua', icon: 'ri-global-line' },
];

const PartnerPage = () => {
  const { t } = useTranslation();
  const header = useScrollAnimation();
  const partner1 = useScrollAnimation({ threshold: 0.1 });
  const partner2 = useScrollAnimation({ threshold: 0.1 });
  const trust = useScrollAnimation({ threshold: 0.1 });
  const cta = useScrollAnimation({ threshold: 0.15 });

  return (
    <main className="min-h-screen bg-white">
      <SeoHead
        title="Professionisti Partner — Discovering Marche | Rete di fiducia per il tuo immobile nelle Marche"
        description="Scopri i professionisti partner di Discovering Marche: studio legale e notarile, impresa edile certificata. Un pacchetto completo per acquistare casa nelle Marche in totale trasparenza."
        keywords="professionisti partner Marche, studio notarile Fermo, impresa edile Camerano, rete professionisti immobiliari, acquisto immobili Marche"
        canonical="/partner"
        jsonLd={PARTNER_JSONLD}
      />

      {/* ===== HERO ===== */}
      <section className="relative h-[50vh] min-h-[380px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20handshake%20between%20professionals%20across%20a%20refined%20wooden%20desk%20with%20contract%20documents%20fountain%20pen%20and%20warm%20ambient%20light%20sophisticated%20Italian%20office%20setting%20with%20soft%20cream%20walls%20and%20dark%20wood%20furniture%20professional%20trust%20atmosphere&width=1920&height=800&seq=partner-hero&orientation=landscape"
            alt="Professionisti Partner Marche — rete di fiducia immobiliare"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-14">
          <p className="font-sans text-xs tracking-widest uppercase text-terracotta-200 mb-4">{t('partner.hero.overline')}</p>
          <h1 className="font-serif text-4xl md:text-6xl text-white font-light max-w-2xl leading-tight">
            {t('partner.hero.title')}
          </h1>
        </div>
      </section>

      {/* ===== INTRO ===== */}
      <section
        ref={header.ref}
        className={`fade-in-up ${header.isVisible ? 'visible' : ''} max-w-4xl mx-auto px-6 py-16 text-center`}
      >
        <p className="font-sans text-base text-anthracite-500 leading-relaxed">{t('partner.intro')}</p>
      </section>

      {/* ===== PARTNER 01 — Studio Spanò ===== */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div
          ref={partner1.ref}
          className={`fade-in-up ${partner1.isVisible ? 'visible' : ''} grid grid-cols-1 lg:grid-cols-2 gap-12 items-center`}
        >
          <div className="rounded-2xl overflow-hidden aspect-[4/3]">
            <img
              src="https://storage.helloreaddy.io/project_files/eb454c64-f721-4c2b-844b-992754102f07/1e0bbc13-d687-459a-9744-37b9fb6cfeed_compressed_5039c438-4640-4cb4-9bcc-8aa642ac7741.webp"
              alt="Studio Spanò — Avvocato e Notaio Fermo"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div>
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-olive-50 text-olive-600 mb-6">
              <i className="ri-file-shield-2-line text-2xl"></i>
            </div>
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-3">
              {t('partner.partner1.overline')}
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-4 leading-snug">
              {t('partner.partner1.title')}
            </h2>
            <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-8">
              {t('partner.partner1.desc')}
            </p>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-olive-50 text-olive-600 font-sans text-xs font-semibold flex-shrink-0 mt-0.5">
                    <i className="ri-check-line"></i>
                  </div>
                  <p className="font-sans text-sm text-anthracite-600 leading-relaxed">{t(`partner.partner1.bullet${i}`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== PARTNER 02 — Sparviero Srl ===== */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div
          ref={partner2.ref}
          className={`fade-in-up ${partner2.isVisible ? 'visible' : ''} grid grid-cols-1 lg:grid-cols-2 gap-12 items-center`}
        >
          <div className="lg:order-2 rounded-2xl overflow-hidden aspect-[4/3] bg-white">
            <img
              src="https://storage.helloreaddy.io/project_files/eb454c64-f721-4c2b-844b-992754102f07/7c0e3e8b-1461-46bb-afa7-4d3e34681960_compressed_ChatGPT-Image-31-lug-2026-20_43_50.webp"
              alt="Sparviero Srl — Impresa di Costruzioni Camerano"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="lg:order-1">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-terracotta-50 text-terracotta-300 mb-6">
              <i className="ri-building-2-line text-2xl"></i>
            </div>
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-3">
              {t('partner.partner2.overline')}
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-4 leading-snug">
              {t('partner.partner2.title')}
            </h2>
            <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-3">
              {t('partner.partner2.details')}
            </p>
            <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-8">
              {t('partner.partner2.desc')}
            </p>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-terracotta-50 text-terracotta-300 font-sans text-xs font-semibold flex-shrink-0 mt-0.5">
                    <i className="ri-check-line"></i>
                  </div>
                  <p className="font-sans text-sm text-anthracite-600 leading-relaxed">{t(`partner.partner2.bullet${i}`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== PERCHÉ UNA RETE DI PARTNER ===== */}
      <section className="bg-ivory-50 py-20 px-6">
        <div ref={trust.ref} className={`fade-in-up ${trust.isVisible ? 'visible' : ''} max-w-5xl mx-auto`}>
          <div className="text-center mb-14">
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">{t('partner.trust.overline')}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light">{t('partner.trust.title')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TRUST_ITEMS.map((item) => (
              <div
                key={item.key}
                className="bg-white rounded-2xl p-8 text-center border border-ivory-200 transition-all duration-300 hover:border-terracotta-200 hover:shadow-sm"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-olive-50 text-olive-600 mx-auto mb-5">
                  <i className={`${item.icon} text-2xl`}></i>
                </div>
                <h3 className="font-serif text-lg text-anthracite-700 mb-3">{t(`partner.trust.${item.key}.title`)}</h3>
                <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{t(`partner.trust.${item.key}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA FINALE ===== */}
      <section className="py-20 px-6">
        <div ref={cta.ref} className={`fade-in-up ${cta.isVisible ? 'visible' : ''} max-w-3xl mx-auto text-center`}>
          <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-6">{t('partner.cta.title')}</h2>
          <p className="font-sans text-sm text-anthracite-500 mb-8 leading-relaxed">{t('partner.cta.text')}</p>
          <Link
            to="/contatti"
            className="inline-block font-sans text-sm tracking-widest uppercase px-10 py-4 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-all duration-300 rounded-full cursor-pointer whitespace-nowrap"
          >
            {t('partner.cta.btn')}
          </Link>
        </div>
      </section>
    </main>
  );
};

export default PartnerPage;